import MuiAppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import type { JSX } from 'react';
import { Fragment, Suspense } from 'react';
import { NavLink } from 'react-router';
import { Icon } from '@/icon/Icon';
import env from '@/util/env';
import { Box } from '../Box';
import SamLogo from '../Logo/SamLogo';

export const appBarHeight = 54;

interface AppBarProps {
  toggleDrawer?: () => void;
  isSmallScreen?: boolean;
  actions?: JSX.Element[];
}

const AppBar = ({
  toggleDrawer = () => {},
  isSmallScreen = false,
  actions = [],
}: AppBarProps) => {
  return (
    <MuiAppBar
      color="transparent"
      position="fixed"
      elevation={0}
      sx={[
        (theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          borderBottom: `1px solid`,
          borderBottomColor: 'divider',
        }),
      ]}
    >
      <Toolbar
        sx={{
          minHeight: `${appBarHeight}px !important`,
          pl: { sm: 3, md: 2 },
          gap: 2,
        }}
      >
        <Left isSmallScreen={isSmallScreen} toggleDrawer={toggleDrawer} />
        <ICPLinks />
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            alignItems: 'center',
            flexFlow: 'row-reverse',
            minWidth: 0,
            height: `${appBarHeight}px`,
          }}
        >
          <Suspense fallback={<></>}>
            {actions.map((action, i) => (
              <Fragment key={i}>{action}</Fragment>
            ))}
          </Suspense>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;

interface AppBarLeftProps {
  isSmallScreen: boolean;
  toggleDrawer: () => void;
}

const Left = ({ isSmallScreen, toggleDrawer }: AppBarLeftProps) => {
  if (isSmallScreen) {
    return (
      <IconButton
        color="inherit"
        aria-label="open drawer"
        onClick={() => toggleDrawer()}
        edge="start"
        size="large"
      >
        <Icon name="menu" />
      </IconButton>
    );
  }

  return (
    <NavLink to="/" style={{ lineHeight: 0 }}>
      <SamLogo />
    </NavLink>
  );
};

const ICPLinks = () => {
  if (!env.ICP_NUMBER && !env.PSB_NUMBER) return null;

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ whiteSpace: 'nowrap' }}
    >
      {env.ICP_NUMBER && (
        <Link
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          underline="hover"
        >
          {env.ICP_NUMBER}
        </Link>
      )}
      {env.ICP_NUMBER && env.PSB_NUMBER && ' | '}
      {env.PSB_NUMBER && (
        <Link
          href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010502044679"
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          underline="hover"
        >
          {env.PSB_NUMBER}
        </Link>
      )}
    </Typography>
  );
};
